import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import Select from '@mulesoft/anypoint-components/lib/Select';
import Button from '@mulesoft/anypoint-components/lib/Button';
import AuthenticationFields from '../AuthenticationFields';
import AdvancedSettingsSection from '../AdvancedSettingsSection';
import {
  TARGET_SYSTEM_OPTIONS,
  ALL_AUTH_METHODS,
  TARGET_SYSTEM_AUTH_COMPATIBILITY
} from '../constants';
import styles from '../ScannerConfigurationForm.css';

/**
 * ConnectionSection Component
 *
 * Renders the connection configuration section with:
 * - Target system selector
 * - Authentication method selector
 * - Authentication credentials fields
 * - Connection test button and status
 * - Advanced settings (email notifications)
 */
const ConnectionSection = ({
  formData,
  connectionStatus,
  showAdvancedSettings,
  onChange,
  onTestConnection,
  onToggleAdvancedSettings,
  onToggleEmailNotifications,
  onAddEmail,
  onRemoveEmail
}) => {
  // Compute auth method options based on selected target system
  const authMethodOptions = useMemo(() => {
    const compatibleMethods = formData.targetSystem
      ? TARGET_SYSTEM_AUTH_COMPATIBILITY[formData.targetSystem] || []
      : [];

    return ALL_AUTH_METHODS.map((method) => ({
      ...method,
      disabled: formData.targetSystem
        ? !compatibleMethods.includes(method.value)
        : false
    }));
  }, [formData.targetSystem]);

  return (
    <section className={styles.section}>
      <div className={styles['section-title']}>Connection Configuration</div>

      <div className={styles.field}>
        <label htmlFor="target-system" className={styles.label}>
          Target System <span className={styles.required}>*</span>
        </label>
        <div className={styles['field-content']}>
          <Select
            id="target-system"
            name="targetSystem"
            testId="target-system"
            value={formData.targetSystem}
            placeholder="Select target system"
            options={TARGET_SYSTEM_OPTIONS}
            onChange={(event) =>
              onChange('targetSystem', event.itemsSelected?.value || '')
            }
            required
          />
        </div>
      </div>

      <div className={styles.field}>
        <label htmlFor="authentication-method" className={styles.label}>
          Authentication method <span className={styles.required}>*</span>
        </label>
        <div className={styles['field-content']}>
          <Select
            id="authentication-method"
            name="authMethod"
            testId="authentication-method"
            value={formData.authMethod}
            placeholder="Select authentication method"
            options={authMethodOptions}
            onChange={(event) =>
              onChange('authMethod', event.itemsSelected?.value || '')
            }
            required
            disabled={!formData.targetSystem}
          />
        </div>
      </div>

      {/* Authentication Credentials (IAM or OAuth) */}
      <AuthenticationFields
        authMethod={formData.authMethod}
        formData={formData}
        onChange={onChange}
      />

      {/* Check Connection Status */}
      {formData.authMethod && (
        <div className={styles.field}>
          <span className={styles['label-text']}>Check connection</span>
          <div className={styles['field-content']}>
            {connectionStatus === 'idle' && (
              <React.Fragment>
                <p className={styles['connection-hint']}>
                  Test connection to verify authentication
                </p>
                <Button
                  type="button"
                  kind="primary"
                  onClick={onTestConnection}
                  data-test-id="test-connection-button"
                >
                  Test connection
                </Button>
              </React.Fragment>
            )}

            {connectionStatus === 'checking' && (
              <Button
                type="button"
                kind="primary"
                disabled
                data-test-id="test-connection-button"
              >
                Testing connection...
              </Button>
            )}

            {connectionStatus === 'success' && (
              <span className={styles['status-success']}>
                ✓ Connection verified successfully
              </span>
            )}
            {connectionStatus === 'error' && (
              <span className={styles['status-error']}>
                ✕ Connection failed: Invalid API Key
              </span>
            )}
          </div>
        </div>
      )}

      {/* Advanced Settings */}
      <AdvancedSettingsSection
        showAdvancedSettings={showAdvancedSettings}
        onToggleAdvancedSettings={onToggleAdvancedSettings}
        emailNotificationsEnabled={formData.emailNotificationsEnabled}
        onToggleEmailNotifications={onToggleEmailNotifications}
        recipientEmails={formData.recipientEmails}
        onAddEmail={onAddEmail}
        onRemoveEmail={onRemoveEmail}
      />
    </section>
  );
};

ConnectionSection.propTypes = {
  /** Form data object containing all form values */
  formData: PropTypes.shape({
    targetSystem: PropTypes.string,
    authMethod: PropTypes.string,
    emailNotificationsEnabled: PropTypes.bool,
    recipientEmails: PropTypes.arrayOf(PropTypes.string)
  }).isRequired,
  /** Current connection test status */
  connectionStatus: PropTypes.oneOf(['idle', 'checking', 'success', 'error'])
    .isRequired,
  /** Whether advanced settings section is expanded */
  showAdvancedSettings: PropTypes.bool.isRequired,
  /** Handler for form field changes */
  onChange: PropTypes.func.isRequired,
  /** Handler for test connection button */
  onTestConnection: PropTypes.func.isRequired,
  /** Handler to toggle advanced settings visibility */
  onToggleAdvancedSettings: PropTypes.func.isRequired,
  /** Handler to toggle email notifications */
  onToggleEmailNotifications: PropTypes.func.isRequired,
  /** Handler to add a new email */
  onAddEmail: PropTypes.func.isRequired,
  /** Handler to remove an email */
  onRemoveEmail: PropTypes.func.isRequired
};

export default ConnectionSection;
